import { useEffect, useState } from "react";
import client from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Cookie, Save, PlayCircle } from "lucide-react";

const KEYS = [
  "onetrust_hostname",
  "onetrust_client_id",
  "onetrust_client_secret",
  "onetrust_geo_rule_group_id",
  "onetrust_scan_page_limit",
  "onetrust_external_org_id",
];

/**
 * OneTrust Cookie Consent settings — OAuth2 client-credentials + the shared
 * Geo Rule Group every newly-provisioned dealer domain gets assigned to.
 * Nothing here is hardcoded: every value is read/written through the same
 * generic `/settings/{key}` endpoints every other integration in this app
 * uses (see DnsIntegrationCard.jsx), gated to System Admin + MHQ Power Admin
 * only — see onetrust_provisioning.py for how these are consumed.
 */
export function OneTrustIntegrationCard() {
  const [values, setValues] = useState(Object.fromEntries(KEYS.map((k) => [k, ""])));
  // Secrets are write-only: the API reports whether one is saved, never its value.
  const [secretSet, setSecretSet] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Count of DDM-migrated dealers sitting in SetupOnNextRun/Error — null while
  // still loading, so the button/message can tell "checking" from "zero".
  const [pendingCount, setPendingCount] = useState(null);
  const [running, setRunning] = useState(false);

  const fetchPendingCount = async () => {
    try {
      const { data } = await client.get("/admin/onetrust-sync/pending-count");
      setPendingCount(data.count);
    } catch {
      setPendingCount(null);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get("/settings");
        const map = Object.fromEntries(data.map((s) => [s.key, s.value]));
        setValues((prev) => Object.fromEntries(KEYS.map((k) => [k, map[k] ?? prev[k] ?? ""])));
        setSecretSet(Object.fromEntries(data.filter((s) => s.is_set).map((s) => [s.key, true])));
      } finally {
        setLoading(false);
      }
    })();
    fetchPendingCount();
  }, []);

  const set = (key) => (e) => setValues((prev) => ({ ...prev, [key]: e.target.value }));

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await client.post("/admin/onetrust-sync/run");
      toast.success(`OneTrust provisioning started for ${data.queued} site${data.queued === 1 ? "" : "s"}`);
      await fetchPendingCount();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start OneTrust sync");
    } finally {
      setRunning(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await Promise.all(KEYS.map((k) => client.put(`/settings/${k}`, { value: (values[k] || "").trim() })));
      toast.success("OneTrust settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="mb-6 p-6" data-testid="settings-onetrust-card">
      <div className="mb-1 flex items-center gap-2">
        <Cookie className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-lg font-semibold">OneTrust Cookie Consent</h2>
      </div>
      <p className="mb-4 text-xs text-muted-foreground">
        Powers automated OneTrust domain provisioning/reconciliation for DDM-migrated dealers — one standalone
        OneTrust website per dealer subdomain, all managed centrally from here (dealers have no OneTrust access).
      </p>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-muted/30 p-3">
        <p className="text-xs text-muted-foreground" data-testid="settings-onetrust-pending-count">
          {pendingCount === null
            ? "Checking pending sites…"
            : `${pendingCount} DDM-managed site${pendingCount === 1 ? "" : "s"} pending provisioning`}
        </p>
        <Button
          data-testid="settings-onetrust-run-now"
          variant="outline"
          size="sm"
          className="gap-1.5"
          disabled={!pendingCount || running}
          onClick={runNow}
        >
          <PlayCircle className="h-3.5 w-3.5" />
          {`Run now for ${pendingCount ?? 0} site${pendingCount === 1 ? "" : "s"}`}
        </Button>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label="Tenant hostname" k="onetrust_hostname" values={values} set={set} loading={loading} saving={saving}
          placeholder="app-eu.onetrust.com" mono
          help="Whichever regional pod your OneTrust tenant lives on — same hostname you log into the OneTrust admin console with" />
        <Field label="Client ID" k="onetrust_client_id" values={values} set={set} loading={loading} saving={saving} mono />
        <Field label="Client secret" k="onetrust_client_secret" values={values} set={set} loading={loading} saving={saving}
          mono type="password" secretSet={secretSet}
          help="OAuth2 client-credentials pair, created in OneTrust under Global Settings → Access Management → Client Credentials" />
        <Field label="Geo Rule Group ID" k="onetrust_geo_rule_group_id" values={values} set={set} loading={loading} saving={saving} mono
          help="Assigned to every newly-provisioned dealer domain — pre-existing domains keep whatever group they already have" />
        <Field label="Scan page limit" k="onetrust_scan_page_limit" values={values} set={set} loading={loading} saving={saving}
          placeholder="50"
          help="Max pages OneTrust crawls when scanning a newly-created dealer domain" />
        <Field label="External org ID" k="onetrust_external_org_id" values={values} set={set} loading={loading} saving={saving}
          placeholder="SAD2800VX" mono
          help="Optional. Sub-org new websites are created under (OneTrust 'externalOrgId'). Required when the API client's own org has no external ID — otherwise creation fails with 'invalid external Id'. Leave empty to use the legacy create endpoint" />
      </div>

      <div className="mt-6 flex justify-end">
        <Button data-testid="settings-onetrust-save" onClick={save} disabled={loading || saving} className="gap-1.5">
          <Save className="h-3.5 w-3.5" />
          Save
        </Button>
      </div>
    </Card>
  );
}

function Field({ label, k, values, set, loading, saving, placeholder, help, mono, type = "text", secretSet }) {
  return (
    <div>
      <Label className="data-label mb-1 block">{label}</Label>
      <Input
        data-testid={`settings-onetrust-${k}`}
        type={type}
        placeholder={secretSet?.[k] && !values[k] ? "•••••••• saved — leave blank to keep" : placeholder}
        value={values[k]}
        onChange={set(k)}
        disabled={loading || saving}
        className={mono ? "font-mono text-xs" : ""}
        autoComplete={type === "password" ? "new-password" : "off"}
      />
      {help && <p className="mt-1 text-[10px] text-muted-foreground">{help}</p>}
    </div>
  );
}
